/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {error} from '@ion/core/src/logger/logger';
import {getToken} from '@ion/core/src/services/SDConnectUtils';
import {decode, encode} from '@ion/core/src/utils/Cipher';
import {ApplicationKeys} from '@/types/application';

/**
  * Retrieves the encrypted session string and returns the decrypted authorization
  * token if the token was created within the last hour.
  */
export const getSession = (): string | undefined => {
  const session = getLocalStorage(ApplicationKeys.BMWPortalSession);
  const time    = getLocalStorage(ApplicationKeys.BMWPortalSessionTime);
  if (session) {
    try {
      if (moment().diff(moment(new Date(parseInt(time))), 'hours') === 0) {
        setSessionTimestamp();
        return decode(session);
      } else {
        deleteSession();
      }
    } catch (err) {
      error('Unable to decode Authorization Token', err);
    }
  }
};

/**
  * Persists the authorization token (as an encrypted session string) as well
  * as the start time of the session.
  */
export const setSession = () => {
  try {
    setLocalStorage(ApplicationKeys.BMWPortalSession, encode(getToken()));
    setSessionTimestamp();
  } catch (err) {
    error('Unable to encode Authorization Token', err);
  }
};

/**
  * Deletes the current authorization token and session timestamp from localStorage.
  */
export const deleteSession = () => {
  try {
    deleteLocalStorage(ApplicationKeys.BMWPortalSession);
    deleteLocalStorage(ApplicationKeys.BMWPortalSessionTime);
  } catch (err) {
    error('Unable to delete Application Session Token from Local Storage', err);
  }
};

/**
 * Allows for setting / resetting the session timestamp. This function is
 * used internally for updating the timestamp of an existing session after
 * the user has been auto-authenticated via their persisted session (as the
 * underlying service expiry is reset with each request).
 */
const setSessionTimestamp = () => {
  setLocalStorage(ApplicationKeys.BMWPortalSessionTime, Date.now());
};
